import {
  CylinderGeometry,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Vector3,
} from "three";

import { isCentralDetailedStreetName } from
  "../../config/oldTownBuildingArt.ts";
import { VISUAL_REALISM_CONFIG } from "../../config/visualRealism.ts";
import type {
  RuntimeLinearFeature,
  RuntimeTreeInstance,
} from "../../world/schema/runtimeTile.ts";
import { streetFeatureRoadEdgeWidthM } from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface StreetTreeBaseBatch {
  readonly object: InstancedMesh;
  readonly instanceCount: number;
}

/**
 * Adds restrained soft tree bases only where source-positioned stems meet a
 * named central street. The exact stem remains authoritative; the disc is a
 * render-only material cue, not a claim about a surveyed pit outline.
 */
export function createStreetTreeBaseBatch(
  tileId: string,
  trees: readonly Readonly<RuntimeTreeInstance>[],
  transportLines: readonly Readonly<RuntimeLinearFeature>[],
  materials: CityMaterialLibrary,
): StreetTreeBaseBatch | null {
  const centralStreetLines = transportLines.filter((feature) =>
    isCentralTreeBaseStreetName(feature.name)
  );
  if (centralStreetLines.length === 0) return null;
  const config = VISUAL_REALISM_CONFIG.centralStreetTreeBase;

  const placements = trees.flatMap((tree) => {
    if (!isSourcePositionedTree(tree)) return [];
    const street = nearestEligibleCentralStreet(
      tree,
      centralStreetLines,
      config.clearanceFromRoadEdgeM,
    );
    return street === null ? [] : [{ tree, streetName: street.name }];
  });
  if (placements.length === 0) return null;

  const geometry = new CylinderGeometry(
    1,
    1,
    config.depthM,
    config.radialSegments,
  );
  const object = new InstancedMesh(
    geometry,
    materials.treeSoil,
    placements.length,
  );
  object.name = `runtime-central-street-tree-bases:${tileId}`;
  object.receiveShadow = true;
  object.castShadow = false;
  object.userData = {
    tileId,
    representation: "source-positioned-soft-tree-base-material-cue",
    treeIds: placements.map(({ tree }) => tree.id),
    treePlacementById: Object.fromEntries(
      placements.map(({ tree }) => [tree.id, tree.placement]),
    ),
    treeSourceIdById: Object.fromEntries(
      placements.map(({ tree }) => [tree.id, tree.sourceId]),
    ),
    streetNameByTreeId: Object.fromEntries(
      placements.map(({ tree, streetName }) => [tree.id, streetName]),
    ),
  };
  const matrix = new Matrix4();
  const rotation = new Quaternion();
  for (const [index, { tree }] of placements.entries()) {
    const radiusM = clamp(
      config.minimumRadiusM + tree.crownRadiusM * 0.045,
      config.minimumRadiusM,
      config.maximumRadiusM,
    );
    rotation.setFromAxisAngle(
      new Vector3(0, 1, 0),
      tree.yawRadians,
    );
    matrix.compose(
      new Vector3(
        tree.position.x,
        tree.baseElevationM + config.surfaceLiftM,
        tree.position.z,
      ),
      rotation,
      // A slight ellipse avoids a repeated token-like silhouette while the
      // authoritative stem remains exactly at its source position.
      new Vector3(radiusM * config.scaleX, 1, radiusM * config.scaleZ),
    );
    object.setMatrixAt(index, matrix);
  }
  object.instanceMatrix.needsUpdate = true;
  object.computeBoundingBox();
  object.computeBoundingSphere();
  return { object, instanceCount: placements.length };
}

function nearestEligibleCentralStreet(
  tree: Readonly<RuntimeTreeInstance>,
  features: readonly Readonly<RuntimeLinearFeature>[],
  clearanceFromRoadEdgeM: number,
): Readonly<{ name: string; distanceM: number }> | null {
  let nearest: Readonly<{ name: string; distanceM: number }> | null = null;
  for (const feature of features) {
    const name = feature.name?.trim();
    if (name === undefined || name.length === 0) continue;
    const distanceM = distanceToLineParts(tree.position, feature.points);
    if (
      distanceM > streetFeatureRoadEdgeWidthM(feature) / 2 +
        clearanceFromRoadEdgeM
    ) {
      continue;
    }
    if (nearest === null || distanceM < nearest.distanceM) {
      nearest = { name, distanceM };
    }
  }
  return nearest;
}

export function isCentralTreeBaseStreetName(
  name: string | undefined,
): boolean {
  return isCentralDetailedStreetName(name);
}

function isSourcePositionedTree(
  tree: Readonly<RuntimeTreeInstance>,
): boolean {
  return tree.placement === "official-municipal-tree-inventory" ||
    tree.placement === "osm-mapped-tree-node" ||
    tree.placement === "reference-derived-street-row";
}

function distanceToLineParts(
  point: Readonly<{ x: number; z: number }>,
  points: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) continue;
    minimum = Math.min(minimum, distanceToSegment(point, from, to));
  }
  return minimum;
}

function distanceToSegment(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): number {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  const amount = lengthSquared <= 1e-9
    ? 0
    : clamp(
        ((point.x - from.x) * deltaX + (point.z - from.z) * deltaZ) /
          lengthSquared,
        0,
        1,
      );
  return Math.hypot(
    point.x - (from.x + deltaX * amount),
    point.z - (from.z + deltaZ * amount),
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
